import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import cookies from 'js-cookie';

import type { ICardForDraft, TDraftCookieHandler } from './models';

const defaultPaymentSystemData = {
    mc: undefined,
    visa: undefined,
    mir: undefined,
};

/**
 * В ситуации когда в форме 2 карты с одинаковым
 * prefillContractId будем добавлять к ним постфикс (например, PDT05-travel).
 * Перед отправкой в анкету или в черновиках нужно получить чистое значение
 */
export const getClearPrefillContractId = (val: string) => val.split('-')[0];

export const getName = (firstName?: string, middleName?: string): string => {
    if (!firstName || !middleName) return '';

    if (middleName === '-') return `${firstName}, `;

    return `${firstName} ${middleName}, `;
};

export const getHref = (draftId?: string) => {
    return `${process.env.DC_DRAFT}?draftId=${draftId}&popupShown=true`;
};

export function determinePaymentSystem(
    card?: ICardForDraft,
    cookieCardId?: string,
    cookiePacketId?: string
) {
    const { cardId = defaultPaymentSystemData, packetId = defaultPaymentSystemData } = card || {};
    const isMcByCardId = cardId.mc === cookieCardId;
    const isVisaByCardId = cardId.visa === cookieCardId;
    const isMcByPacketId = packetId.mc === cookiePacketId;
    const isVisaByPacketId = packetId.visa === cookiePacketId;
    const isMirByPacketId = packetId.mir === cookiePacketId;
    const isMirByCardId = cardId.mir === cookieCardId;

    if (isMcByCardId && isMcByPacketId) {
        return 'mc';
    }
    if (isVisaByCardId && isVisaByPacketId) {
        return 'visa';
    }
    if (isMirByCardId && isMirByPacketId) {
        return 'mir';
    }

    return undefined;
}

function compareCard(
    card: ICardForDraft,
    cardId?: string,
    packetId?: string,
    prefillContractId?: string
): boolean {
    const clearPrefillContractId = getClearPrefillContractId(card.id);

    if (clearPrefillContractId !== prefillContractId) return false;

    if (
        (card.packetId.mc === packetId && card.cardId.mc === cardId) ||
        (card.packetId.mir === packetId && card.cardId.mir === cardId) ||
        (card.packetId.visa === packetId && card.cardId.visa === cardId)
    ) {
        return true;
    }

    return false;
}

export const draftCookieHandler: TDraftCookieHandler = ({ cards, cookie, showDraftAnalytics }) => {
    if (!cookie) {
        return undefined;
    }

    const { cardId, draftId, firstName, middleName, packetId, prefillContractId } = cookie;

    const cardFromCookie = cards?.find(card =>
        compareCard(card, cardId, packetId, prefillContractId)
    );

    if (!cardFromCookie) {
        return undefined;
    }

    let cardLabelFromCookie = cardFromCookie ? cardFromCookie.label : undefined;

    const paymentSystem = determinePaymentSystem(cardFromCookie, cardId, packetId);

    const paySystemLabel = (paySystem: string) => {
        if (paySystem === 'mc') {
            return ' | Mastercard';
        }

        if (paySystem === 'mir') {
            return ' | МИР';
        }

        return '| Visa';
    };

    if (cardLabelFromCookie && paymentSystem) {
        cardLabelFromCookie = `${cardLabelFromCookie}${paySystemLabel(paymentSystem)}`;
    }

    if (!cardLabelFromCookie) {
        return undefined;
    }

    if (showDraftAnalytics) {
        sendEvent(showDraftAnalytics);
    }

    return {
        showDraft: true,
        cardLabel: cardLabelFromCookie,
        userName: getName(firstName, middleName),
        agreeHref: getHref(draftId),
    };
};

export const getDraftCookie = (cookieName: string) => {
    return cookies.get(cookieName);
};
